import React, { lazy, Suspense } from 'react';
import { RouteObject } from 'react-router-dom';
import LoadingSpinner from '../components/common/LoadingSpinner';
import PrivateRoute from '../components/auth/PrivateRoute';
// Import admin routes from admin-system
import { adminRoutes } from '../../admin-system/frontend/router';
import AdminPrivateRoute from '../../admin-system/frontend/components/AdminPrivateRoute';

import MobileAppPage from '../pages/mobile/MobileAppPage';
import MobileLoginPage from '../pages/mobile/MobileLoginPage';
import MobilePrivateRoute from '../components/auth/MobilePrivateRoute';

const TenantProfile = lazy(() => import('../pages/accounts/TenantProfile'));
const LandlordProfile = lazy(() => import('../pages/accounts/LandlordProfile'));
const BusinessProfile = lazy(() => import('../pages/accounts/BusinessProfile'));
const PublicProfile = lazy(() => import('../pages/accounts/PublicProfile'));
const ChatBot = lazy(() => import('../components/Chatbot/ChatBot'));
const ContractTemplatePage = lazy(() => import('../pages/contracts/ContractTemplatePage'));

// Lazy load other components
const Login = lazy(() => import('../pages/auth/LoginPage'));
const Register = lazy(() => import('../pages/auth/RegisterPage'));
const ForgotPassword = lazy(() => import('../pages/auth/ForgotPasswordPage'));
const ResetPassword = lazy(() => import('../pages/auth/ResetPasswordPage'));
const KYCPage = lazy(() => import('../pages/auth/KYCPage'));
const KYCStatusPage = lazy(() => import('../pages/auth/KYCStatusPage'));
const ProfileRouter = lazy(() => import('../pages/accounts/ProfileRouter'));

const AccountManagement = lazy(() => import('../pages/quanlytaikhoan/AccountManagement'));
const AccountUpgrade = lazy(() => import('../pages/quanlytaikhoan/AccountUpgradePage'));
const EarningPage = lazy(() => import('../pages/Earning/EarningPage'));
const ProductDetail = lazy(() => import('../pages/product/ProductDetail'));
const AddProductPage = lazy(() => import('../pages/add-product/AddProduct'));
const EditProductPage = lazy(() => import('../pages/edit-product/EditProduct'));
const PolicyPage = lazy(() => import('../pages/policy/PolicyPage'));
const RoomPayPage = lazy(() => import('../pages/pay/RoomPayPage'));
const SDGsPage = lazy(() => import('../pages/SDGs 17Goal/SDGsPage'));
const ShareholderRelations = lazy(() => import('../pages/shareholder/ShareholderRelations'));
const Home = lazy(() => import('../pages/home/Home'));
const MobileSessionBridgePage = lazy(() => import('../pages/auth/MobileSessionBridgePage'));

// Simple 404 component
const NotFound = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <h1 className="text-6xl font-bold text-gray-900 mb-4">404</h1>
      <p className="text-xl text-gray-600 mb-8">Trang bạn tìm kiếm không tồn tại</p>
      <a 
        href="/" 
        className="inline-block px-6 py-3 bg-[#C4A35A] text-white rounded-lg hover:bg-[#9A7B2E] transition-colors"
      >
        Về trang chủ
      </a>
    </div>
  </div>
);

// Wrap component with Suspense
const withSuspense = (Component: React.ComponentType) => (
  <Suspense fallback={<LoadingSpinner />}>
    <Component />
  </Suspense>
);

// Auth routes that don't use MainLayout
const authRoutes: RouteObject[] = [
  {
    path: '/auth',
    children: [
      {
        path: 'login',
        element: withSuspense(Login),
      },
      {
        path: 'register',
        element: withSuspense(Register),
      },
      {
        path: 'forgot-password',
        element: withSuspense(ForgotPassword),
      },
      {
        path: 'reset-password',
        element: withSuspense(ResetPassword),
      },
      {
        path: 'kyc',
        element: <PrivateRoute>{withSuspense(KYCPage)}</PrivateRoute>,
      },
      {
        path: 'kyc-status',
        element: <PrivateRoute>{withSuspense(KYCStatusPage)}</PrivateRoute>,
      },
      {
        path: 'mobile-session',
        element: withSuspense(MobileSessionBridgePage),
      },
    ],
  },
];

// Public routes
const publicRoutes: RouteObject[] = [
  {
    path: '/',
    element: withSuspense(Login),
  },
  {
    path: 'login',
    element: withSuspense(Login),
  },
  {
    path: 'register',
    element: withSuspense(Register),
  },
  {
    path: '/home',
    element: <AdminPrivateRoute>{withSuspense(Home)}</AdminPrivateRoute>,
  },
  {
    path: 'agent',
    element: withSuspense(ChatBot),
  },
  {
    path: 'account',
    element: withSuspense(AccountManagement),
  },
  {
    path: 'account-management',
    element: withSuspense(AccountManagement),
  },
  {
    path: 'earning',
    element: withSuspense(EarningPage),
  },

  {
    path: 'detail/:id',
    element: withSuspense(ProductDetail),
  },
  {
    path: 'add-product',
    element: withSuspense(AddProductPage),
  },
  {
    path: 'edit-product/:id',
    element: <PrivateRoute>{withSuspense(EditProductPage)}</PrivateRoute>,
  },
  {
    path: 'pay/:roomCode',
    element: withSuspense(RoomPayPage),
  },
  {
    path: '/policy',
    element: withSuspense(PolicyPage),
  },
  {
    path: 'sdgs',
    element: withSuspense(SDGsPage),
  },
  {
    path: 'shareholder',
    element: withSuspense(ShareholderRelations),
  },
  {
    path: '/profile/public/:userId',
    element: withSuspense(PublicProfile),
  },
];

// Protected routes that require authentication
const protectedRoutes: RouteObject[] = [
  {
    path: '/contracts/:contractId',
    element: <PrivateRoute>{withSuspense(ContractTemplatePage)}</PrivateRoute>,
  },
  {
    path: '/contracts',
    element: <PrivateRoute>{withSuspense(ContractTemplatePage)}</PrivateRoute>,
  },
  {
    path: '/profile',
    element: <PrivateRoute>{withSuspense(ProfileRouter)}</PrivateRoute>,
  },
  {
    path: '/profile/tenant',
    element: <PrivateRoute>{withSuspense(TenantProfile)}</PrivateRoute>,
  },
  {
    path: '/profile/landlord',
    element: <PrivateRoute>{withSuspense(LandlordProfile)}</PrivateRoute>,
  },
  {
    path: '/profile/business',
    element: <PrivateRoute>{withSuspense(BusinessProfile)}</PrivateRoute>,
  },
  {
    path: '/mobile/login',
    element: <MobileLoginPage />,
  },
  {
    path: '/mobile/app',
    element: (
      <MobilePrivateRoute>
        <MobileAppPage />
      </MobilePrivateRoute>
    ),
  },
  {
    path: '/upgrade/:planId',
    element: <PrivateRoute>{withSuspense(AccountUpgrade)}</PrivateRoute>,
  },
];

// Catch-all route for 404
const notFoundRoute: RouteObject = {
  path: '*',
  element: <NotFound />,
};

// Combine all routes - include admin routes from admin-system
export const appRoutes: RouteObject[] = [
  ...authRoutes, 
  ...publicRoutes, 
  ...protectedRoutes, 
  ...adminRoutes, // Admin routes from admin-system
  notFoundRoute
]; 